import { ApiError, normalizeDrfError } from "@/lib/api/errors";
import { readBody, throwIfError } from "@/lib/api/parseResponse";
import { clientFetch } from "@/lib/api/client";
import { proxyPath } from "@/lib/api/paths";
import {
  ALLAUTH_LOGIN_PROXY,
  ALLAUTH_SIGNUP_PROXY,
  ALLAUTH_SESSION_PROXY,
  ALLAUTH_PASSWORD_REQUEST_PROXY,
  ALLAUTH_PASSWORD_RESET_PROXY,
  ALLAUTH_VERIFY_EMAIL_PROXY,
  ALLAUTH_RESEND_VERIFY_EMAIL_PROXY,
} from "@/lib/api/paths";

// Client-side counterpart to lib/api/users.ts's server-only fetchMe — used to
// populate the sessionStorage auth cache (see useAuthUser) right after a
// successful login/signup, instead of waiting on a fresh server render to
// tell every mounted nav who's logged in.
//
// Three-state result (mirrors fetchMe's ok/unauthenticated/error split) rather than
// collapsing everything to CachedAuthUser | null: a transient failure on this call —
// network blip, cookie-propagation race — is not the same thing as a confirmed 401/403,
// and callers need to tell them apart. Collapsing both to null once caused every mounted
// nav to flash "logged out" seconds after a login that had actually just succeeded,
// because the caller (AuthModal) wrote null into the shared cache on any failure.
export type GetCurrentUserResult =
  | { status: "ok"; user: { name: string; email: string } }
  | { status: "unauthenticated" }
  | { status: "error" };

export async function getCurrentUser(): Promise<GetCurrentUserResult> {
  try {
    const { res, body } = await clientFetch(proxyPath("v1/users/me/"));
    if (res.status === 401 || res.status === 403) return { status: "unauthenticated" };
    if (!res.ok) return { status: "error" };
    return { status: "ok", user: body as { name: string; email: string } };
  } catch {
    return { status: "error" };
  }
}

type SignupPayload = { name: string; email: string; password: string; password2: string };
type LoginPayload = { email: string; password: string };

type AllauthErrorEntry = { code?: string; param?: string; message?: string };

export function normalizeAllauthError(
  payload: unknown,
  status: number,
): ReturnType<typeof normalizeDrfError> {
  if (
    payload &&
    typeof payload === "object" &&
    "errors" in payload &&
    Array.isArray((payload as Record<string, unknown>).errors)
  ) {
    const errors = (payload as { errors: AllauthErrorEntry[] }).errors;
    const fieldErrors: Record<string, string[]> = {};
    let firstMessage = "";

    for (const entry of errors) {
      const msg = entry.message ?? `Request failed (${status})`;
      if (!firstMessage) firstMessage = msg;
      if (entry.param) {
        (fieldErrors[entry.param] ??= []).push(msg);
      }
    }

    return {
      message: firstMessage || `Request failed (${status})`,
      code: undefined,
      fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
      missingRequiredFields: undefined,
    };
  }
  return normalizeDrfError(payload, status);
}

function isPendingEmailVerification(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const data = (body as Record<string, unknown>).data;
  if (!data || typeof data !== "object") return false;
  const flows = (data as Record<string, unknown>).flows;
  return (
    Array.isArray(flows) &&
    flows.some(
      (f: unknown) =>
        f &&
        typeof f === "object" &&
        (f as Record<string, unknown>).id === "verify_email" &&
        (f as Record<string, unknown>).is_pending !== false,
    )
  );
}

async function authFetch(path: string, body?: unknown): Promise<unknown> {
  const { res, body: responseBody } = await clientFetch(path, {
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  throwIfError(res, responseBody, normalizeAllauthError);
  return responseBody;
}

export type SignupResult = { pendingVerification: true } | { pendingVerification: false };

export async function signup(
  payload: SignupPayload,
  opts?: { turnstileToken?: string },
): Promise<SignupResult> {
  const { res, body: responseBody } = await clientFetch(ALLAUTH_SIGNUP_PROXY, {
    method: "POST",
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      password2: payload.password2,
      name: payload.name,
    }),
    ...(opts?.turnstileToken
      ? { headers: { "cf-turnstile-token": opts.turnstileToken } }
      : {}),
  });

  // Mandatory verification: allauth returns 401 with verify_email flow pending.
  if (res.status === 401 && isPendingEmailVerification(responseBody)) {
    return { pendingVerification: true };
  }

  throwIfError(res, responseBody, normalizeAllauthError);
  return { pendingVerification: false };
}

export type LoginResult = { pendingVerification: true } | { pendingVerification: false };

export async function login(
  payload: LoginPayload,
  opts?: { turnstileToken?: string },
): Promise<LoginResult> {
  const { res, body: responseBody } = await clientFetch(ALLAUTH_LOGIN_PROXY, {
    method: "POST",
    body: JSON.stringify({ email: payload.email, password: payload.password }),
    ...(opts?.turnstileToken
      ? { headers: { "cf-turnstile-token": opts.turnstileToken } }
      : {}),
  });

  // Unverified user attempting to log in: allauth returns 401 with verify_email flow.
  if (res.status === 401 && isPendingEmailVerification(responseBody)) {
    return { pendingVerification: true };
  }

  throwIfError(res, responseBody, normalizeAllauthError);
  return { pendingVerification: false };
}

export async function verifyEmail(code: string): Promise<void> {
  await authFetch(ALLAUTH_VERIFY_EMAIL_PROXY, { key: code });
}

export async function resendVerification(): Promise<void> {
  const { res, body } = await clientFetch(ALLAUTH_RESEND_VERIFY_EMAIL_PROXY, {
    method: "POST",
  });
  throwIfError(res, body, normalizeAllauthError);
}

export async function requestPasswordReset(payload: {
  email: string;
}): Promise<unknown> {
  return authFetch(ALLAUTH_PASSWORD_REQUEST_PROXY, { email: payload.email });
}

export type ResetPasswordResult =
  | { pendingVerification: true }
  | { pendingVerification: false };

/**
 * Submit the new password for a password reset.
 *
 * Verified users: allauth returns 200 + session (auto-login). Returns { pendingVerification: false }.
 * Unverified users: allauth changes the password, then returns 401 with a verify_email flow
 *   (and sends the verification code). Returns { pendingVerification: true }.
 */
export async function resetPassword(payload: {
  key: string;
  password: string;
}): Promise<ResetPasswordResult> {
  const { res, body: responseBody } = await clientFetch(ALLAUTH_PASSWORD_RESET_PROXY, {
    method: "POST",
    body: JSON.stringify({ key: payload.key, password: payload.password }),
  });

  if (res.status === 401 && isPendingEmailVerification(responseBody)) {
    return { pendingVerification: true };
  }

  throwIfError(res, responseBody, normalizeAllauthError);
  return { pendingVerification: false };
}

/**
 * Validate a password-reset key before showing the set-password form.
 * Calls GET on the reset endpoint with the key in the X-Password-Reset-Key header.
 * Returns true if the key is valid; returns false (does not throw) if expired/invalid.
 * Throws ApiError on unexpected non-400 errors.
 */
export async function validateResetKey(key: string): Promise<boolean> {
  const { res, body } = await clientFetch(ALLAUTH_PASSWORD_RESET_PROXY, {
    method: "GET",
    headers: { "x-password-reset-key": key },
  });
  if (res.ok) return true;
  if (res.status === 400) return false;
  const { message, code, fieldErrors } = normalizeAllauthError(body, res.status);
  throw new ApiError({ status: res.status, message, code, fieldErrors });
}

export async function logout(): Promise<void> {
  const res = await fetch(ALLAUTH_SESSION_PROXY, {
    method: "DELETE",
    credentials: "include",
  });
  // allauth headless returns 401 after a successful logout (user is now unauthenticated).
  // Treat it — and 200 — as success; throw only on unexpected errors.
  if (!res.ok && res.status !== 401) {
    const body = await readBody(res);
    const { message, code, fieldErrors } = normalizeAllauthError(body, res.status);
    throw new ApiError({ status: res.status, message, code, fieldErrors });
  }
}
